import { makeAutoObservable } from 'mobx';
import { makePersistable } from 'mobx-persist-store';

import { IMAGES_BY_COMPANY } from '~/constants/images';
import {
  CERISE_PHONE_COUNTRIES,
  HEIMKAUP_PHONE_COUNTRIES,
  type PhoneCountriesConfig,
} from '~/utils/countryPhoneCodes';

import { storage as localStorage } from '../stores/LocalStorage';
import { CeriseConfig } from './configs/Carrefour';
import { CircleKConfig } from './configs/CircleK';
import { CityDrinksConfig } from './configs/CityDrinks';
import { DubaiExpressConfig } from './configs/DubaiExpress';
import { DubaiExpressComConfig } from './configs/DubaiExpressCom';
import { ElectroniumConfig } from './configs/Electronium';
import { HeimkaupConfig } from './configs/Heimkaup';
import { JiffyConfig } from './configs/Jiffy';
import { KidsConfig } from './configs/Kids';
import { LocaleeConfig } from './configs/Localee';
import { MCosmeticConfig } from './configs/MCosmetic';
import { OcadoConfig } from './configs/Ocado';
import { OskuhusConfig } from './configs/Oskuhus';
import { StorrdConfig } from './configs/Storrd';
import { ViloConfig } from './configs/Vilo';
import {
  type CompanyConfig,
  type CompanyFeatures,
  CompanyName,
  type EnvVariables,
} from './interface';

export const DEFAULT_COMPANIES = [
  CompanyName.Vilo,
  CompanyName.Jiffy,
  CompanyName.Kids,
  CompanyName.Oskuhus,
];

/**
 * FEATURE FLAG (JS-14143): courier delivery alongside PUDO for tenants whose
 * both fulfilment types share one `delivery_method` (EMU).
 *
 * This is only the frontend half of the gate — the backend owns a per-company
 * `isCourierDeliveryEnabled` config on top of it, and both must say yes (see
 * `hasDeliveryTypeSwitcher`). Listing a company here does not turn the feature
 * on by itself; it only allows the tenant to ask the backend about it.
 *
 * Kill switch: empty this list to turn the feature off regardless of what the
 * backend says. The tenant then falls back to the pre-feature PUDO-only flow
 * (`isPudoOnly`): no switcher in the address modal, no `destinationKind` sent
 * to ETA/orders (the backend falls back to zone-based resolution on its own),
 * no courier zone matching. Existing orders keep rendering correctly — their
 * type is read from `destination_kind` stored on the order, which is not gated.
 */
const COURIER_DELIVERY_COMPANIES: string[] = [CompanyName.MCosmetic];

const DEFAULT_CASH_BANKNOTES = [100, 200, 500, 1000];

class Company implements CompanyFeatures {
  private currentCompany: CompanyName = CompanyName.CityDrinks;
  private isOriginalCompany: boolean = true;
  private variables: EnvVariables = {};
  private readonly configs: Record<CompanyName, CompanyConfig> = {
    [CompanyName.CityDrinks]: CityDrinksConfig,
    [CompanyName.Localee]: LocaleeConfig,
    [CompanyName.CircleK]: CircleKConfig,
    [CompanyName.Vilo]: ViloConfig,
    [CompanyName.Jiffy]: JiffyConfig,
    [CompanyName.Kids]: KidsConfig,
    [CompanyName.Electronium]: ElectroniumConfig,
    [CompanyName.Oskuhus]: OskuhusConfig,
    [CompanyName.Heimkaup]: HeimkaupConfig,
    [CompanyName.Cerise]: CeriseConfig,
    [CompanyName.DubaiExpress]: DubaiExpressConfig,
    [CompanyName.DubaiExpressCom]: DubaiExpressComConfig,
    [CompanyName.Storrd]: StorrdConfig,
    [CompanyName.MCosmetic]: MCosmeticConfig,
    [CompanyName.Ocado]: OcadoConfig,
  };

  isActiveCaptchaDevMode = false;

  /**
   * Backend half of the courier-delivery gate: the delivery-service config
   * `isCourierDeliveryEnabled`, resolved per company. Set during the SSR
   * preload and read back from `__INITIAL_STATE__.context` on the client, so
   * both renders agree. Defaults to `false` — courier delivery stays off until
   * the backend says otherwise.
   */
  private isCourierDeliveryEnabledByConfig: boolean = false;

  constructor() {
    makeAutoObservable(this);

    makePersistable(this, {
      name: 'Company',
      properties: ['isActiveCaptchaDevMode'],
      storage: localStorage,
    }).catch((error) => error && console.error(error));

    if (import.meta.env.SSR) {
      return;
    }

    // In Storybook environment, __INITIAL_STATE__ might not be available yet
    // when Company constructor is called, so we skip initialization.
    // Storybook decorators will call setCompanyName() later.
    const isStorybook =
      typeof window !== 'undefined' &&
      document.getElementById('storybook-root') !== null;

    if (isStorybook && !window.__INITIAL_STATE__?.context) {
      return;
    }

    const contextData = window.__INITIAL_STATE__.context;

    if (import.meta.env.REACT_APP_ENV !== 'production') {
      this.setIsOriginalCompanyLogic(!!contextData.isOriginalLogic);
    }

    this.setCompanyName(contextData.company);
    this.setVariables(contextData.variables as EnvVariables);
    this.setIsCourierDeliveryEnabled(!!contextData.isCourierDeliveryEnabled);

    const rootDiv = document && document?.getElementById('app');
    rootDiv?.setAttribute('data-company', `${contextData.company}`);
  }

  setCompanyName(name: CompanyName) {
    this.currentCompany = name;
  }

  setVariables(variables: EnvVariables) {
    this.variables = variables;
  }

  setIsCourierDeliveryEnabled(enabled: boolean) {
    this.isCourierDeliveryEnabledByConfig = enabled;
  }

  setIsOriginalCompanyLogic(yes: boolean) {
    this.isOriginalCompany = yes;
  }

  setIsActiveCaptchaDevMode(active: boolean) {
    this.isActiveCaptchaDevMode = active;
  }

  get config(): CompanyConfig {
    if (this.isOriginalCompany) {
      const baseConfig = this.configs[this.currentCompany];
      if (this.variables.REACT_APP_DEFAULT_WAREHOUSE) {
        return {
          ...baseConfig,
          warehouse: {
            ...baseConfig.warehouse,
            default: this.variables.REACT_APP_DEFAULT_WAREHOUSE,
          },
        };
      }
      return baseConfig;
    } else {
      const company = { ...this.configs[this.currentCompany] };
      //**Use default company id LOCALEE if we check only UI and no other logic! */
      company.id = 'd05f30ba-ceb5-4633-a3b6-aa0f9463954f';

      company.warehouse = {
        default: 'NJSB1',
        minimalOrderFeeWarehouseCodes: [],
        location: { lat: 40.8537291, lng: -74.0197964 },
      };

      company.map = {
        center: { lat: 40.89753813649296, lng: -74.09670413033002 },
        zoom: 12,
      };

      return company;
    }
  }

  get name(): CompanyName {
    return this.currentCompany;
  }

  get envVariables(): EnvVariables {
    return this.variables;
  }

  get images() {
    return IMAGES_BY_COMPANY[this.currentCompany];
  }

  get currency() {
    return this.config.currency;
  }

  get currencyCode() {
    return this.config.currencyCode;
  }

  get isMobileMoneyPaymentEnabled(): boolean {
    return this.config.availablePaymentMethods?.includes('pvit') ?? false;
  }

  get isDisableLoyaltyProgram(): boolean {
    return [''].includes(this.currentCompany);
  }

  get isExceptionAlcohol(): boolean {
    return ![''].includes(this.currentCompany);
  }

  get hasAddress2(): boolean {
    return [''].includes(this.currentCompany);
  }

  get hasNoteAddress2(): boolean {
    return !this.isIncludedIn([CompanyName.MCosmetic]);
  }

  get isTrimTrailingZeros(): boolean {
    return ![''].includes(this.currentCompany);
  }

  get hasWelcomeBlock(): boolean {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  /**
   * Offers a 3x candidate in image `srcset`s instead of stopping at 2x.
   *
   * Current phones report a device pixel ratio of 3, so a 2x cap leaves them
   * upscaling every image by half again — measured on a 393px viewport, 1179
   * device pixels of slot filled from a 780px file. MCosmetic reported that as
   * broken image quality on hero banners, where full-bleed artwork makes the
   * softness plain.
   *
   * Off by default because it is not free: on MCosmetic's own mobile home page
   * the category and banner set measured 4652 KB at 2x and 8097 KB at 3x. Turn
   * it on for a tenant whose imagery is the product, not for one whose pages
   * live or die on LCP.
   */
  get highDensityImagesEnabled(): boolean {
    return this.isIncludedIn([CompanyName.MCosmetic]);
  }

  get isAvailableCartInMobileHeader(): boolean {
    return !this.isIncludedIn(DEFAULT_COMPANIES);
  }

  get showETAInfoMobileHeader(): boolean {
    return this.isIncludedIn(DEFAULT_COMPANIES);
  }

  get showPersonalDataButtonMobileCabinetPage() {
    return this.isIncludedIn(DEFAULT_COMPANIES);
  }

  get showLocationIconMobileCabinetPage() {
    return this.isIncludedIn(DEFAULT_COMPANIES);
  }

  get showSectionsMobileProductPage() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get showSearchHeaderProductPage(): boolean {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get showShowShareButtonsProductPage(): boolean {
    return this.isIncludedIn(DEFAULT_COMPANIES);
  }

  get showActionButtonsMobileProductPage() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get showShortReviewListMobileProductPage() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get showDescriptionReviewProductPage() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get customMobileRatingProductPage() {
    return this.isIncludedIn(DEFAULT_COMPANIES);
  }

  get useStaticDeliveryEta() {
    return this.isIncludedIn([CompanyName.DubaiExpressCom]);
  }

  get useShortWeekdayDeliveryLabel() {
    return this.isIncludedIn([CompanyName.Localee]);
  }

  get showOrderTrackingMap() {
    return !this.isIncludedIn([CompanyName.DubaiExpressCom]);
  }

  /**
   * Shows the originally promised delivery window (ETA) on the order cards of the
   * history tab, the way active orders already show it, instead of the warehouse
   * ship-by deadline.
   */
  get showOriginalEtaInOrderHistory() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get showReferralProgram() {
    return !this.isIncludedIn([
      CompanyName.DubaiExpressCom,
      CompanyName.DubaiExpress,
      CompanyName.MCosmetic,
      CompanyName.Localee,
    ]);
  }

  get hideFavoritesForGuests() {
    return this.isIncludedIn([CompanyName.MCosmetic, CompanyName.Localee]);
  }

  get hideAboutUs() {
    return this.isIncludedIn([
      ...DEFAULT_COMPANIES,
      CompanyName.MCosmetic,
      CompanyName.Heimkaup,
      CompanyName.Cerise,
    ]);
  }

  get hideContactUs() {
    return this.isIncludedIn([
      ...DEFAULT_COMPANIES,
      CompanyName.Heimkaup,
      CompanyName.Cerise,
      CompanyName.Localee,
    ]);
  }

  get nonCloseableNavMobileShopPage() {
    return this.isIncludedIn(DEFAULT_COMPANIES);
  }

  get showCardDisclaimer() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  /** GDPR cookie consent banner shown on the first visit */
  get showCookieBanner() {
    return this.isIncludedIn([
      CompanyName.DubaiExpress,
      CompanyName.DubaiExpressCom,
      CompanyName.Heimkaup,
    ]);
  }

  get hasWoltDeliveryProvider() {
    return this.isIncludedIn([CompanyName.Heimkaup]);
  }

  get hasDeepCatalog() {
    return [CompanyName.MCosmetic].includes(this.currentCompany);
  }

  get showCategorySEOBlock() {
    return !this.isIncludedIn([CompanyName.DubaiExpress]);
  }

  // DubaiExpress: adding a card is impossible with the current processing.
  get showSavedPaymentCards() {
    return !this.isIncludedIn([CompanyName.DubaiExpress]);
  }

  get promocodeInInputResetCheckout() {
    return this.isIncludedIn([
      CompanyName.Localee,
      CompanyName.Oskuhus,
      CompanyName.CityDrinks,
    ]);
  }

  get loyaltyProgramEnabled() {
    return this.isIncludedIn([
      CompanyName.Jiffy,
      CompanyName.Localee,
      CompanyName.Cerise,
      CompanyName.Heimkaup,
      CompanyName.CircleK,
      CompanyName.Ocado,
      CompanyName.MCosmetic,
    ]);
  }

  /** Simplified promocode flow for MCosmetic company (without verification request) */
  get simplifiedPromocodeFlow() {
    return this.isIncludedIn([CompanyName.MCosmetic]);
  }

  /** Mindbox loyalty integration: customer balance via /customer/loyalty-points */
  get mindboxLoyaltyEnabled() {
    return this.isIncludedIn([CompanyName.MCosmetic]);
  }

  /**
   * Customer memberships in the cabinet. Switched off for every company on
   * the storefront for now (the CMS keeps managing CityDrinks plans); kept
   * off production until the purchase flow ships.
   */
  get isMembershipsEnabled() {
    if (import.meta.env.REACT_APP_ENV === 'production') {
      return false;
    }

    return this.isIncludedIn([]);
  }

  get authAgreementFieldsEnabled() {
    return this.isIncludedIn([CompanyName.MCosmetic]);
  }

  /**
   * Referral program is enabled when the essential config.referral fields are
   * populated; downloadAppLink is gated separately (desktop QR panel only)
   */
  get isReferralProgramEnabled(): boolean {
    const { code, discount, minBasket } = this.config.referral ?? {};
    return Boolean(code && discount && minBasket);
  }

  get hasPromocodeCDBanner() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get buttonOutPanelCheckout() {
    return this.isIncludedIn(DEFAULT_COMPANIES);
  }

  get needVerification() {
    return this.isIncludedIn([CompanyName.CityDrinks, CompanyName.Heimkaup]);
  }

  get showButtonInTitleAddress() {
    return this.isIncludedIn(DEFAULT_COMPANIES);
  }

  get showCartMobileSubtotal() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get showMobileDowloadPanel() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get showExpressDeliveryBannerMainPage() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get useAwsPaymentMethod() {
    return this.isIncludedIn([CompanyName.CircleK]);
  }

  get isSommelierEnabled() {
    return false;
  }

  get isDeliveryInfoModalEnabled() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get showCompanyLogoOnMainMobile() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get showAdditionalTextEmptyPayments() {
    return this.isIncludedIn([CompanyName.Localee]);
  }

  get deliveryMethodSelectionAvailable() {
    return this.isIncludedIn([
      ...DEFAULT_COMPANIES,
      CompanyName.Heimkaup,
      CompanyName.Cerise,
      CompanyName.CircleK,
      CompanyName.Localee,
    ]);
  }

  get isShareBlockCaptionVisible() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get isShareBlockButtonIconVisible() {
    return this.isIncludedIn([CompanyName.Localee]);
  }

  /**
   * Use prices converted to the company currency (e.g. USD → RUB)
   * from `salePrice` / `convertedDiscountPrice` instead of the base price.
   */
  get convertedPricesEnabled() {
    return this.isIncludedIn([CompanyName.DubaiExpress]);
  }

  get mixAndMatchEnabled(): boolean {
    return this.isDubaiExpressCompany;
  }

  /**
   * Structural allow-list for the pickup-slots feature (self-collection time
   * windows), mirroring the CMS `hasPickupSlotsSettings` gate. The backend response
   * fields that drive the feature (`pickup.pickupSlotsEnabled`, the
   * `PICKUP_SLOT_REQUIRED` rejection code) are not scoped to a company on their own,
   * so this is the only thing stopping another tenant from picking up the feature if
   * the backend ever sends those fields for them.
   */
  get hasPickupSlots() {
    return this.isIncludedIn([CompanyName.Heimkaup]);
  }

  get needKycCheck() {
    return this.match({
      citydrinks: true,
      localee: false,
      vilo: false,
      jiffy: false,
      ourkids: false,
      electronium: false,
      oskuhus: false,
      heimkaup: false,
      default: false,
    });
  }

  get hasOnboarding() {
    return !this.isIncludedIn([CompanyName.Cerise]);
  }

  get hasOnboardingAutoplay() {
    return !this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get hasOnboardingCloseButton() {
    return !this.isIncludedIn([CompanyName.CityDrinks, CompanyName.Localee]);
  }

  get hasOnboardingDotsInHeader() {
    return !this.isIncludedIn([CompanyName.CityDrinks, CompanyName.Localee]);
  }

  get hasNewOnboardingDesign() {
    return this.isIncludedIn([
      CompanyName.CityDrinks,
      CompanyName.Storrd,
      CompanyName.MCosmetic,
    ]);
  }

  get hasOnboardingDotsAboveButtons() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get showStartShopping() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get showCartModalCount() {
    return this.isIncludedIn([CompanyName.Localee, CompanyName.CircleK]);
  }

  get showCartAdditionalTextMobile() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get missAgeRestrictionModal() {
    return this.isIncludedIn([
      CompanyName.CircleK,
      CompanyName.Vilo,
      CompanyName.Kids,
      CompanyName.Oskuhus,
      CompanyName.Cerise,
      CompanyName.DubaiExpress,
      CompanyName.DubaiExpressCom,
      CompanyName.Storrd,
      CompanyName.MCosmetic,
      CompanyName.Electronium,
      CompanyName.Localee,
    ]);
  }

  get useCommodityGroupAgeCheck() {
    return this.isIncludedIn([CompanyName.CircleK]);
  }

  get hideReviews() {
    return this.isIncludedIn([
      CompanyName.CircleK,
      CompanyName.Cerise,
      CompanyName.CityDrinks,
      CompanyName.Heimkaup,
      CompanyName.Electronium,
    ]);
  }

  get hideFreeDeliveryProgressBar() {
    return false; // TODO: uncomment - this.currentCompany === CompanyName.CityDrinks;
  }

  get showCartCoutToRemove() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get addressModalDefault() {
    return this.isIncludedIn(DEFAULT_COMPANIES);
  }

  get combineItemsByDefaut() {
    return this.isIncludedIn([CompanyName.CityDrinks, CompanyName.Localee]);
  }

  /** Per-SKU cap on the number of units a customer may add to the cart;
   *  `undefined` means no company-specific cap (only stock/backorder limits apply) */
  get maxQuantityPerSku(): number | undefined {
    return this.match({ [CompanyName.Heimkaup]: 30 });
  }

  /** Cap on the total number of units allowed in a single order */
  get maxItemsPerOrder(): number | undefined {
    return undefined;
  }

  get isDefaultCompany() {
    return this.isIncludedIn(DEFAULT_COMPANIES);
  }

  get showPartiallyPickedInfo() {
    return !this.isIncludedIn([CompanyName.Vilo]);
  }

  get hideFreezeMoneyMessage() {
    return this.isIncludedIn([CompanyName.Vilo]);
  }

  get hideDownloadAppPanel() {
    return this.isIncludedIn([CompanyName.CircleK, CompanyName.Kids]);
  }

  get isEnabledCaptcha() {
    return (
      import.meta.env.REACT_APP_ENV === 'production' ||
      this.isActiveCaptchaDevMode
    );
  }

  get useBarcode() {
    return this.isIncludedIn([CompanyName.CircleK]);
  }

  get hideOrderMap() {
    return this.isIncludedIn([CompanyName.MCosmetic]);
  }

  /** Hides the receipt link on the order page. */
  get hideOrderReceipt() {
    return this.isIncludedIn([CompanyName.DubaiExpress]);
  }

  get isReviewPanelHidden() {
    return this.isIncludedIn([CompanyName.CircleK]);
  }

  get showLinkedinIcon() {
    return this.isIncludedIn([CompanyName.CircleK]);
  }

  get isEnabledIndexNow() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get isEnabledIntercom() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get isEnabledIndexParametrizedUrls() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get isFavsInCabinet() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get showMatchingProducts_ProductPage() {
    return this.isIncludedIn([]);
  }

  get showMatchingProducts_CartPage() {
    return this.isIncludedIn([
      CompanyName.CityDrinks,
      CompanyName.MCosmetic,
      ...DEFAULT_COMPANIES,
    ]);
  }

  get showSimilarProducts_ProductPage() {
    return this.isIncludedIn([
      CompanyName.CityDrinks,
      CompanyName.DubaiExpress,
      CompanyName.DubaiExpressCom,
      CompanyName.MCosmetic,
      CompanyName.Jiffy,
      CompanyName.Heimkaup,
      CompanyName.Cerise,
    ]);
  }

  get showFrequentlyBoughtTogether_ProductPage() {
    return this.isIncludedIn([CompanyName.Cerise]);
  }

  get isDefaultHeaderInCatalog() {
    return !this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get isEnabledIconOnAddToCartButton() {
    return this.isIncludedIn([CompanyName.Cerise]);
  }

  get hasNewCheckoutElemtnts() {
    return this.isIncludedIn([
      CompanyName.CityDrinks,
      CompanyName.Storrd,
      CompanyName.MCosmetic,
    ]);
  }

  get isFooterAppDownloadButtonsEnabled() {
    return this.isIncludedIn([
      CompanyName.CircleK,
      CompanyName.Heimkaup,
      CompanyName.Cerise,
    ]);
  }

  get isFavsTabEnabled() {
    return this.isIncludedIn([
      CompanyName.MCosmetic,
      CompanyName.CircleK,
      CompanyName.Localee,
      CompanyName.Heimkaup,
      CompanyName.Cerise,
      CompanyName.Electronium,
      CompanyName.DubaiExpress,
      CompanyName.DubaiExpressCom,
    ]);
  }

  get isVisibleHeaderCategories() {
    return !this.isIncludedIn([CompanyName.Heimkaup]);
  }

  get showSubheaderCategories(): boolean {
    return this.isIncludedIn([CompanyName.Cerise, CompanyName.DubaiExpress]);
  }

  /**
   * Shows the brand-colored "Catalog" button in the desktop header. Hidden for
   * DubaiExpress: its design uses the inline category menu (subheader) instead.
   */
  get showHeaderCatalogButton(): boolean {
    return !this.isIncludedIn([CompanyName.DubaiExpress]);
  }

  get showHeaderLoyaltyChip(): boolean {
    return this.isIncludedIn([CompanyName.Cerise]);
  }

  get showMobileHeaderFavs(): boolean {
    return !this.isIncludedIn([CompanyName.Cerise, CompanyName.Heimkaup]);
  }

  get showMobileHeaderLogo(): boolean {
    return this.isIncludedIn([CompanyName.Localee]);
  }

  /**
   * Shows the language chip in the mobile header. Tenants that open in their
   * own language (see `preferCompanyLanguage`) otherwise have no way to switch
   * it on mobile: the mobile footer has no language slot and the cabinet
   * picker is behind login.
   */
  get showMobileHeaderLanguageChip(): boolean {
    return this.isIncludedIn([CompanyName.Heimkaup]);
  }

  get isExpressDeliveryOffByDefault() {
    return this.isIncludedIn([CompanyName.CityDrinks, CompanyName.Localee]);
  }

  get canDefaultAddressBeEmpty() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get hasAddressPinGuard() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  /**
   * Tenants whose address flow is the delivery/pickup modal (see
   * EDIT_ADDRESS_*_MODULE in components/ModalList/modalMap.ts, which resolves
   * the modal from this getter). That modal has no "location name" field, so
   * every address is saved with the default 'home' type and each point is
   * named after its own street and house number instead.
   */
  get usesDeliveryPickupAddressModal() {
    return this.isIncludedIn([CompanyName.Heimkaup]);
  }

  /**
   * Tenants whose in-store QR codes carry a warehouse code that preselects
   * that store as the pickup address (see services/PickupStoreLink.ts).
   * Requires click-and-collect zones, so it is scoped to the tenants that
   * actually have them rather than left on for everyone.
   */
  get hasPickupStoreLink() {
    return this.isIncludedIn([CompanyName.Heimkaup]);
  }

  get showNewAddressModal() {
    return this.isIncludedIn([
      CompanyName.CityDrinks,
      CompanyName.MCosmetic,
      CompanyName.Heimkaup,
      CompanyName.Cerise,
      CompanyName.Localee,
      CompanyName.CircleK,
      CompanyName.Electronium,
    ]);
  }

  get isPudoOnly() {
    return this.isIncludedIn([CompanyName.MCosmetic]);
  }

  /**
   * Tenants allowed to run courier delivery at all (see
   * COURIER_DELIVERY_COMPANIES). Also decides who asks the backend for its
   * `isCourierDeliveryEnabled` config — nobody else spends a request on it.
   */
  get isCourierDeliveryFeatureEnabled() {
    return this.isIncludedIn(COURIER_DELIVERY_COMPANIES);
  }

  /**
   * Companies that deliver both to a PUDO point and to a customer address, so
   * the address modal offers a courier/PUDO switcher instead of a single mode.
   * Every courier-delivery behaviour cascades from this getter.
   *
   * Both halves of the gate must agree: the frontend feature flag
   * (COURIER_DELIVERY_COMPANIES) and the backend's per-company
   * `isCourierDeliveryEnabled` config. Either one off means PUDO-only.
   */
  get hasDeliveryTypeSwitcher() {
    return (
      this.isCourierDeliveryFeatureEnabled &&
      this.isCourierDeliveryEnabledByConfig
    );
  }

  /**
   * Tenants that also offer partner (EMU) pickup points alongside their own
   * PUDO zones. Partner points come from the delivery service directory, not
   * from `eta/v2/api/zones`, and are ordered with `pup_type: 'partner'`.
   */
  get hasPartnerPickupPoints() {
    return this.isIncludedIn([CompanyName.MCosmetic]);
  }

  /**
   * Delivery provider an order to a partner pickup point is served by. Sent
   * with the cart calculation and the order instead of the tenant's own
   * provider — without it the calculation answers 500.
   */
  get partnerPickupDeliveryProvider(): string | undefined {
    return this.match({
      [CompanyName.MCosmetic]: 'EMU',
      default: undefined,
    });
  }

  /**
   * Pickup points come from the external wms-connector service (with their own
   * UUID) instead of delivery zones. Drives the ПВЗ selection flow.
   */
  get isExternalPickupPoints() {
    return this.isIncludedIn([CompanyName.DubaiExpress]);
  }

  /**
   * Companies where an address is tagged with how the order is received
   * (`usage: pickup | delivery`). That tag — not the delivery zone — is what
   * the UI reads the fulfilment type from.
   */
  get tracksAddressUsage(): boolean {
    return this.showDeliveryMethodSwitcher || this.hasDeliveryTypeSwitcher;
  }

  get showAddressSubtitle() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get hasContactSupport() {
    return this.isIncludedIn([CompanyName.CityDrinks, CompanyName.Electronium]);
  }

  /** Floating support launcher pinned to the bottom-right corner on desktop */
  get hasFloatingSupportButton() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get hasAddressInFooter() {
    return this.config.address && this.isIncludedIn([CompanyName.Heimkaup]);
  }

  get showWorkingHoursInFooter() {
    return (
      this.config.workingHours && this.isIncludedIn([CompanyName.Heimkaup])
    );
  }

  get tileSizeEnabled() {
    return !this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get isEnabledOutOfZoneModal() {
    return !this.isPudoOnly;
  }

  get isGeofencingEnabled() {
    return this.isIncludedIn([CompanyName.Heimkaup, CompanyName.CityDrinks]);
  }

  get showGeofencingNotice() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get redirectCatalogToShop() {
    return this.isIncludedIn([
      CompanyName.DubaiExpressCom,
      CompanyName.CircleK,
    ]);
  }

  /** Storefront runs on `config.defaultCity`; the address is picked at checkout. */
  get addressSelectedAtCheckout() {
    return this.isIncludedIn([
      CompanyName.DubaiExpress,
      CompanyName.DubaiExpressCom,
    ]);
  }

  /**
   * Tenants whose address field in the V3 edit-address modal accepts typing
   * and shows Google Places suggestions. `config.useAutocompleteAddress`
   * additionally fills the address fields from the picked suggestion; the
   * companies listed here keep reverse-geocoding the picked coordinates, so
   * the normalized address and the house number still come from the geocoder.
   */
  get canSearchAddress(): boolean {
    return (
      !!this.config.useAutocompleteAddress ||
      this.addressSelectedAtCheckout ||
      this.isIncludedIn([CompanyName.Cerise])
    );
  }

  get isDubaiExpressCompany() {
    return this.isIncludedIn([
      CompanyName.DubaiExpress,
      CompanyName.DubaiExpressCom,
    ]);
  }

  /**
   * Variant selector redesign (JS-14252): image swatches for `containsImage`
   * properties and selectable unavailable variants (a value with no offer for
   * the current combination switches the selection to an existing offer).
   * Other tenants keep the legacy chips where unreachable combos are inert.
   */
  get variantSwatchesEnabled() {
    return this.isDubaiExpressCompany;
  }

  /**
   * Product page requests `compactOffers=true`: one offer in full plus
   * `variants[]`, the rest loaded on demand via `offers?full=true`. Off for
   * every tenant for now; the full `offers[]` response is requested instead.
   */
  get compactProductOffersEnabled() {
    return false;
  }

  /**
   * Shows customs duty / parcel weight & dimensions block on the PDP (cross-border goods).
   * Customs fees are delivered by the backend alongside the converted prices, so the
   * block only makes sense for companies that run on converted prices.
   */
  get showProductCustomsInfo() {
    return this.convertedPricesEnabled;
  }

  /**
   * Shows the "Express" badge on product cards, cart cards, the PDP and the
   * product modal. Disabled for external pickup-point companies, whose parcels
   * arrive in days via 5Post, and for companies whose design drops the badge.
   */
  get showExpressBadge() {
    return !this.isIncludedIn([
      CompanyName.DubaiExpress,
      CompanyName.DubaiExpressCom,
      CompanyName.CityDrinks,
      CompanyName.Heimkaup,
    ]);
  }

  get showOnlyDiscountBadgeOnProductCards() {
    return this.is(CompanyName.MCosmetic);
  }

  /**
   * i18next context for order-status labels. `'pickup'` swaps the courier-delivery
   * wording ("out for delivery") for pickup-point wording ("on the way to the
   * pickup point") on external-pickup companies; `undefined` keeps the default.
   */
  get orderStatusContext(): 'pickup' | undefined {
    return this.isExternalPickupPoints ? 'pickup' : undefined;
  }

  /**
   * External carrier tracking-page URL for an order (GBS for DubaiExpress),
   * built from `links.deliveryTracking` + the fulfilment order number.
   * Returns null when the company has no external tracking page.
   */
  getOrderTrackingUrl(orderNumber: Nullable<string>): string | null {
    const base = this.config.links.deliveryTracking;
    if (!base || !orderNumber) {
      return null;
    }
    return `${base}${encodeURIComponent(orderNumber)}`;
  }

  /** SaaS V3 client companies (white-label storefronts on V3 design system) */
  get isSaas() {
    return this.isIncludedIn([
      CompanyName.Localee,
      CompanyName.CircleK,
      CompanyName.Cerise,
      CompanyName.Heimkaup,
      CompanyName.Electronium,
    ]);
  }

  /** Hides cabinet (personal area) routes for marketplace companies */
  get hideCabinetRoutes() {
    return false;
  }

  /** Forces specific UI language regardless of user preference */
  get forceLanguage(): string | undefined {
    return this.match({ [CompanyName.DubaiExpress]: 'ru' });
  }

  /** Uses company default language instead of browser/cookie language */
  get preferCompanyLanguage() {
    return this.isIncludedIn([CompanyName.Heimkaup]);
  }

  get useVerticalProductImageSlider() {
    return this.isIncludedIn([CompanyName.DubaiExpress, CompanyName.MCosmetic]);
  }

  /**
   * Whether the home page requests CMS blocks that hold no products, such as
   * the DubaiExpress trust row and platform figures built from child
   * categories; the catalog drops empty categories otherwise.
   */
  get homeIncludesEmptyCategories() {
    return this.isIncludedIn([CompanyName.DubaiExpress]);
  }

  /**
   * Whether the home page shows the CMS hero banner (landing page Hero
   * banner). The DubaiExpress home opens straight with its own blocks, so it
   * neither requests nor renders the banner.
   */
  get heroBannerEnabled() {
    return (
      this.newDesignSystemEnabled &&
      !this.isIncludedIn([CompanyName.DubaiExpress])
    );
  }

  get useReviewsOnProductPage() {
    return this.isIncludedIn([
      CompanyName.DubaiExpress,
      CompanyName.MCosmetic,
      CompanyName.Ocado,
    ]);
  }

  /** Falls back to default warehouse when product has no warehouse */
  get useDefaultWarehouseWhenMissing() {
    return this.isIncludedIn([CompanyName.DubaiExpress]);
  }

  /** Displays prices as integers without decimal places */
  get useIntegerCurrency() {
    return this.isIncludedIn([CompanyName.DubaiExpress]);
  }

  /** The cart drawer that compact product cards open instead of adding a second unit. */
  get hasCartDrawer() {
    return this.isIncludedIn([CompanyName.DubaiExpress]);
  }

  /** Catalog pages show the description of their top-level category as a note under the breadcrumbs. */
  get catalogZoneNoteEnabled() {
    return this.isIncludedIn([CompanyName.DubaiExpress]);
  }

  /** Search results and favorites list products with the compact product card. */
  get hasCompactProductCards() {
    return this.isIncludedIn([CompanyName.DubaiExpress]);
  }

  /** Rating mark on the compact product card. */
  get compactCardRatingIcon(): 'camel' | 'star' {
    return this.isIncludedIn([CompanyName.DubaiExpress]) ? 'camel' : 'star';
  }

  /** Tax label used in cart, checkout and order totals (`VAT`, `Sales TAX`, ...) */
  get taxName() {
    return this.config.taxName ?? 'VAT';
  }

  /** Preset cash amounts offered on the checkout cash-payment step */
  get cashBanknotes() {
    return this.config.cashBanknotes ?? DEFAULT_CASH_BANKNOTES;
  }

  /** Hides the VAT/tax line in cart, checkout and order receipt (price already includes VAT) */
  get hideVat() {
    return this.isIncludedIn([CompanyName.DubaiExpress, CompanyName.MCosmetic]);
  }

  /**
   * Customer email is optional: checkout and personal-data forms accept an
   * empty value and the order is created without `recipient.email`.
   */
  get isCustomerEmailOptional() {
    return this.isIncludedIn([CompanyName.MCosmetic]);
  }

  /** External URL for cart redirect instead of in-app cart */
  get externalCartUrl(): string | undefined {
    return undefined;
  }

  /** Translation key for external cart button label */
  get externalCartLabelKey(): string | undefined {
    return undefined;
  }

  /** Shows feedback widget inside products grid on catalog page */
  get insertGridFeedbackInProductsGrid() {
    return false;
  }

  get hasPaymentSystemsV3(): boolean {
    return this.isIncludedIn([CompanyName.MCosmetic]);
  }

  get showHeaderExpressBanner() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  /** Uses LMS orders/v3 calculate and new-order flow with route selection */
  get hasNewLogistics(): boolean {
    return this.isIncludedIn([
      CompanyName.DubaiExpress,
      CompanyName.DubaiExpressCom,
    ]);
  }

  get showCheckoutAddressRestrictionBanner() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get hasDeliverEarlierToggle() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  /** Controls orders icon/dropdown visibility in HeaderV3 */
  get showHeaderOrders() {
    return true;
  }

  /** Controls delivery address display in HeaderV3 */
  get showHeaderAddress() {
    return true;
  }

  /** Controls user action buttons (login/cabinet/cart) in HeaderV3 */
  get showHeaderUserActions() {
    return true;
  }

  /** Controls promotional banners section in HeaderV3 */
  get showHeaderBanners() {
    return false;
  }

  get keyboardOverlaysContent() {
    return this.isIncludedIn([CompanyName.MCosmetic]);
  }

  /**
   * Every brand is on design system v3. The v2 brands (vilo, jiffy, ourkids,
   * oskuhus) are no longer served, and their component set has been removed, so
   * this is a constant rather than a per-brand list. Kept as a getter because
   * ~50 call sites read it as a behavioural flag.
   */
  get newDesignSystemEnabled() {
    return true;
  }

  /**
   * Countries offered by the auth phone field, `undefined` where the plain input
   * stays. A list renders a picker in front of the field so that the calling code
   * is chosen rather than typed; a single country renders a static prefix instead.
   */
  get phoneCountries(): PhoneCountriesConfig | undefined {
    return this.match({
      [CompanyName.Heimkaup]: HEIMKAUP_PHONE_COUNTRIES,
      [CompanyName.Cerise]: CERISE_PHONE_COUNTRIES,
    });
  }

  get phoneCountrySelectEnabled(): boolean {
    return !!this.phoneCountries;
  }

  get showDeliveryMethodSwitcher(): boolean {
    return this.isIncludedIn([CompanyName.Heimkaup]);
  }

  get isUpdateRequiredModalEnabled() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get showOrderAgainSlider() {
    return this.isIncludedIn([
      CompanyName.CityDrinks,
      CompanyName.Cerise,
      CompanyName.Heimkaup,
    ]);
  }

  get showTopPicksSlider() {
    return this.isIncludedIn([CompanyName.CityDrinks]);
  }

  get hasAgeRestriction() {
    return this.isIncludedIn([CompanyName.Heimkaup]);
  }

  get isCountryCodeLocked() {
    return this.isIncludedIn([CompanyName.Heimkaup]);
  }

  get showProductRecommendations() {
    return this.isIncludedIn([
      CompanyName.Cerise,
      CompanyName.Heimkaup,
      CompanyName.Jiffy,
    ]);
  }

  get showProductImageZoom() {
    return !this.isIncludedIn([
      CompanyName.Heimkaup,
      CompanyName.DubaiExpress,
      CompanyName.DubaiExpressCom,
    ]);
  }

  get hideVolumeTitleOnProductPage() {
    return this.isIncludedIn([CompanyName.Heimkaup]);
  }

  get autoOpenAddressModalOnFirstVisit() {
    return this.isIncludedIn([CompanyName.Heimkaup]);
  }

  /**
   * Tenants whose address form saves without a house number. Read by the
   * address modals (no asterisk, save enabled) and by the checkout button,
   * which otherwise sends the customer back to the form to add one.
   */
  get isBuildingOptional() {
    return this.isIncludedIn([
      CompanyName.Heimkaup,
      CompanyName.Localee,
      CompanyName.Cerise,
    ]);
  }

  get zonePolygonHidden() {
    // Delivery-zone polygons are a dev/QA visualisation aid: show them for every
    // company on non-production (dev/staging/testing). In production hide them
    // for CityDrinks and Heimkaup so real users don't see the zone overlay.
    if (import.meta.env.REACT_APP_ENV !== 'production') {
      return false;
    }

    return this.isIncludedIn([CompanyName.CityDrinks, CompanyName.Heimkaup]);
  }

  get isNewPaymentMethodFormEnabled() {
    return false;
  }

  /** Renders all maps via the Yandex Maps JS API instead of Google Maps */
  get isYandexMaps() {
    return this.isIncludedIn([CompanyName.DubaiExpress, CompanyName.MCosmetic]);
  }

  get isOctoSavedCardsEnabled() {
    return true;
  }

  get shouldNavigateToOrdersPageAfterOrder() {
    return this.isIncludedIn([CompanyName.Heimkaup]);
  }

  isCompanyNameExist(name: string): boolean {
    return this.configs[name as CompanyName] !== undefined;
  }

  isIncludedIn(nameList: string[]): boolean {
    return nameList.includes(this.currentCompany);
  }

  // Return type: T if passed all companies or default, else T | undefined
  // Priority: exact company > v3 (if newDesignSystemEnabled) > default
  match<T>(record: { default?: T } & Record<CompanyName, T>): T;
  match<T>(record: { default: T; v3?: T } & Partial<Record<CompanyName, T>>): T;
  match<T>(
    record: { default?: T; v3?: T } & Partial<Record<CompanyName, T>>,
  ): T | undefined;
  match<T>(
    record: { default?: T; v3?: T } & Partial<Record<CompanyName, T>>,
  ): T | undefined {
    if (this.currentCompany in record) {
      return record[this.currentCompany];
    }
    if ('v3' in record && this.newDesignSystemEnabled) {
      return record.v3;
    }
    return record.default;
  }

  // Return type: T if passed all design variants or default, else T | undefined
  variant<T>(record: { default?: T } & Record<'1' | '2', T>): T;
  variant<T>(record: { default: T } & Partial<Record<'1' | '2', T>>): T;
  variant<T>(record: Partial<Record<'1' | '2' | 'default', T>>): T | undefined;
  variant<T>(
    record: { default?: T } & Partial<Record<'1' | '2', T>>,
  ): T | undefined {
    return this.isIncludedIn([CompanyName.CityDrinks])
      ? record['1']
      : record['2'];
  }

  is(name: `${CompanyName}`): boolean {
    return this.name === name;
  }
}

export const company = new Company();
